import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuthStorage } from '../common/hooks/useAuthStorage';
import { useNavigate } from 'react-router-dom';
import { useUserService } from '../services/userService';
import { REFRESH_TOKEN, TOKEN, USER_ROLES } from '../common/constants/localStorage';
import { updateToken } from '../services/tokenService';
import { useAuthService } from '../services/authService';
import globalNotification from '../common/hooks/globalNotification';
import { useTeamService } from '../services/teamService';
import { routesPath } from '../common/constants/routesPath';
import { isTwoFactorPending } from '../utils/twoFactorSession';
import { isImpersonationTab, replaceImpersonationToken } from '../services/impersonationSession';
import { stopImpersonation, switchImpersonatedTeam } from '../services/impersonationService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useAuthStorage(TOKEN, null);
    const [refreshToken, setRefreshToken] = useAuthStorage(REFRESH_TOKEN, null);
    const [currentUser, setCurrentUser] = useState();
    const [isUserLoading, setIsUserLoading] = useState(true);

    const navigate = useNavigate();
    const userService = useUserService();
    const authService = useAuthService();
    const { updateMembership, findUserMembership } = useTeamService();

    const fetchUser = useCallback(async () => {
        setIsUserLoading(true);
        try {
            // a 2FA user's password-only token is refused by /auth/me until the TOTP step is done; asking
            // would 401, fail the refresh (there is no refresh token yet) and end the sign-in. An impersonation
            // token is never that, whatever the target's own 2FA: the tab must load the target
            if (token && (isImpersonationTab() || !isTwoFactorPending(token))) {
                const { data } = await userService.getUser();

                setCurrentUser(data);
                updateToken(USER_ROLES, data.userRoleNames);

                // an impersonated user without a team stays read-only in the shell, never in the create-team flow
                if (!data.teamId && !isImpersonationTab()) {
                    navigate(routesPath.ROOT.CREATE_TEAM_AUTH);
                }
            }
        } catch (e) {
            // in an impersonation tab the axios interceptor ends the session on a 401; never run the admin logout
            if (!isImpersonationTab()) {
                logout();
            }
            console.error(e);
        } finally {
            setIsUserLoading(false);
        }
    }, []);

    const setAuth = (token, refreshToken) => {
        setToken(token);
        setRefreshToken(refreshToken);
    };

    const setAccessToken = token => {
        setToken(token);
    };

    const logout = async () => {
        // signing out of an impersonation tab ends the impersonation; the admin's own session is not touched
        if (isImpersonationTab()) {
            await stopImpersonation();
            return;
        }

        try {
            if (!currentUser) {
                throw new Error();
            }

            const { data } = await findUserMembership(currentUser.id);

            if (!data) {
                throw new Error();
            }

            await updateMembership({ id: data.id, isActive: false });
        } catch (e) {
            console.log(e);
        } finally {
            authService.logout();
            updateToken(TOKEN, null);
            updateToken(REFRESH_TOKEN, null);
            updateToken(USER_ROLES, null);
            navigate('/auth/sign-in');
        }
    };

    const changeTeam = async teamId => {
        try {
            if (isImpersonationTab()) {
                // same session and expiry, another of the impersonated user's teams; nothing is written to the user
                const { accessToken } = await switchImpersonatedTeam(teamId);
                replaceImpersonationToken(accessToken);
                setAccessToken(accessToken);
                return;
            }

            const { data } = await authService.changeTeam(teamId);

            setAccessToken(data.accessToken);
        } catch (error) {
            globalNotification.open({ message: 'This membership is not valid' });
        } finally {
            await fetchUser();
        }
    };

    useEffect(() => {
        if (token) {
            fetchUser();
        }
    }, [token]);

    return (
        <AuthContext.Provider
            value={{
                token,
                setAuth,
                logout,
                fetchUser,
                currentUser,
                isUserLoading,
                changeTeam,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
